import { MoreHorizontal } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { ColorType } from "../../../configs/constants/ColorType";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { AppTextStyleProps } from "../../Core/AppText/AppText";

const POPOVER_WIDTH_PX = 200;

export interface PopOverAction {
	text: string;
	textStyles?: AppTextStyleProps;
	action: any;
	disabled?: boolean;
	divider?: "top" | "bottom";
}

export interface ActionsPopOverProps {
	actions?: PopOverAction[];
	label?: string;
	width?: number;
	/** Merged onto the menu content, e.g. a z-index override when the menu sits
	 * inside a dialog/sheet (their overlays are z-[1200]). */
	contentClassName?: string;
}

/** Maps the legacy AppText colour names used by callers onto Tailwind tokens. */
const TEXT_COLOR_CLASS: Partial<Record<NonNullable<ColorType>, string>> = {
	danger: "text-danger",
	green: "text-success",
	actionBlue: "text-primary",
	grayText: "text-muted-foreground",
	grayTextDark: "text-muted-foreground",
	grayTextLight: "text-muted-foreground",
};

/**
 * "⋯" context menu with a list of actions.
 *
 * Built on the shadcn dropdown-menu primitive so the menu is portalled to
 * <body> and never clipped by a scrolling sidebar/table cell, and gets proper
 * keyboard navigation, outside-click and Escape handling for free.
 */
const ActionsPopOver: React.FC<ActionsPopOverProps> = ({
	actions,
	label,
	width = POPOVER_WIDTH_PX,
	contentClassName,
}) => {
	const { t } = useTranslation();

	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					aria-label={label ? undefined : (t("MORE") as string)}
					className="my-auto inline-flex min-h-[20px] min-w-[40px] cursor-pointer select-none items-center justify-center gap-1 rounded-md bg-transparent px-1 text-primary outline-none transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-ring"
				>
					<MoreHorizontal className="h-5 w-5" />
					{label && <span className="text-sm font-medium">{t(label as any)}</span>}
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end" style={{ width }} className={cn("p-1", contentClassName)}>
				{actions?.map((actionItem, index) => {
					const colorClass = actionItem.textStyles?.color
						? TEXT_COLOR_CLASS[actionItem.textStyles.color]
						: undefined;

					return (
						<React.Fragment key={index}>
							{actionItem.divider === "top" && <DropdownMenuSeparator />}
							<DropdownMenuItem
								disabled={actionItem.disabled || false}
								onSelect={() => actionItem.action()}
								className={cn("py-2 font-medium", colorClass)}
							>
								{actionItem.text && t(actionItem.text as any)}
							</DropdownMenuItem>
							{actionItem.divider === "bottom" && <DropdownMenuSeparator />}
						</React.Fragment>
					);
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ActionsPopOver;
